import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Trophy, Users2, Calendar, ChevronRight } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useLoading } from "../context/LoadingContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

const CATEGORY_META = {
  normal: { label: "Normal", icon: Calendar, color: "#8B5CF6" },
  competition: { label: "Competition", icon: Trophy, color: "#F59E0B" },
  teamwork: { label: "Teamwork", icon: Users2, color: "#10B981" },
};

export default function Events() {
  const [events, setEvents] = useState(null);
  const [types, setTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", event_type_id: "", category: "normal", scheduled_at: "" });
  const { hasPerm } = useAuth();
  const { withLoading } = useLoading();
  const navigate = useNavigate();

  const load = async () => {
    const [e, t] = await Promise.all([api.get("/events"), api.get("/event-types")]);
    setEvents(e.data); setTypes(t.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title || !form.scheduled_at) return toast.error("Title and date are required");
    await withLoading(api.post("/events", { ...form, attendee_ids: [] }), "Scheduling event");
    toast.success("Event scheduled");
    setOpen(false);
    setForm({ title: "", description: "", event_type_id: "", category: "normal", scheduled_at: "" });
    load();
  };

  return (
    <div>
      <PageHeader
        title="Events"
        subtitle="Schedule competitions, teamwork sessions and community events."
        action={hasPerm("manage_events") && <Button data-testid="schedule-event-btn" onClick={() => setOpen(true)} className="gap-2 bg-purple-600 hover:bg-purple-500"><Plus className="h-4 w-4" /> Schedule Event</Button>}
      />

      {!events ? (
        <div className="grid gap-4 lg:grid-cols-2">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-36 rounded-2xl bg-slate-800/50" />)}</div>
      ) : events.length === 0 ? (
        <div className="card-glass flex flex-col items-center rounded-2xl py-16 text-center">
          <Calendar className="h-10 w-10 text-slate-600" />
          <p className="mt-3 text-sm text-slate-400">No events scheduled yet.</p>
        </div>
      ) : (
        <div className="grid gap-4 lg:grid-cols-2">
          {events.map((e, i) => {
            const meta = CATEGORY_META[e.category] || CATEGORY_META.normal;
            const present = e.attendees?.filter((a) => a.status === "present").length || 0;
            return (
              <motion.button
                key={e.id}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.05 }}
                onClick={() => navigate(`/events/${e.id}`)}
                data-testid={`event-card-${e.id}`}
                className="card-glass group rounded-2xl p-5 text-left transition-all hover:-translate-y-1 hover:shadow-[0_0_24px_rgba(139,92,246,0.2)]"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-2">
                    <span className="rounded-lg p-2" style={{ background: `${meta.color}22`, color: meta.color }}><meta.icon className="h-5 w-5" /></span>
                    <div>
                      <p className="font-semibold text-white">{e.title}</p>
                      <p className="text-xs text-slate-400">{new Date(e.scheduled_at).toLocaleString()}</p>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-slate-600 transition-transform group-hover:translate-x-1 group-hover:text-purple-400" />
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
                  <span className="rounded-full px-2.5 py-1 font-medium capitalize" style={{ background: `${meta.color}18`, color: meta.color }}>{meta.label}</span>
                  {e.event_type && <span className="rounded-full px-2.5 py-1 font-medium" style={{ background: `${e.event_type.color}18`, color: e.event_type.color }}>{e.event_type.name}</span>}
                  <span className="rounded-full border border-slate-700 px-2.5 py-1 capitalize text-slate-400">{e.status}</span>
                  <span className="ml-auto text-slate-400">{present}/{e.attendees?.length || 0} present</span>
                </div>
              </motion.button>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-slate-800 bg-[#121620] text-slate-100" data-testid="schedule-event-dialog">
          <DialogHeader><DialogTitle>Schedule Event</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Title *</Label><Input data-testid="event-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 bg-slate-900/60" /></div>
            <div><Label className="text-xs">Description</Label><Textarea data-testid="event-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1 bg-slate-900/60" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category *</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="event-category" className="mt-1 bg-slate-900/60"><SelectValue /></SelectTrigger>
                  <SelectContent className="border-slate-800 bg-[#181D2A] text-slate-100">
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="competition">Competition</SelectItem>
                    <SelectItem value="teamwork">Teamwork</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Event Type</Label>
                <Select value={form.event_type_id} onValueChange={(v) => setForm({ ...form, event_type_id: v })}>
                  <SelectTrigger data-testid="event-type" className="mt-1 bg-slate-900/60"><SelectValue placeholder="Select type" /></SelectTrigger>
                  <SelectContent className="border-slate-800 bg-[#181D2A] text-slate-100">
                    {types.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Scheduled Date & Time *</Label><Input data-testid="event-datetime" type="datetime-local" value={form.scheduled_at} onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })} className="mt-1 bg-slate-900/60" /></div>
          </div>
          <DialogFooter><Button data-testid="submit-event-btn" onClick={submit} className="bg-purple-600 hover:bg-purple-500">Schedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
